import { buildQuoteProducts, quoteProductSummary } from './quoteProducts';

const dateOnly = value => value ? String(value).slice(0, 10) : '';

// Same fallback the detail panel's products table uses: a product without its own
// principal/category shows the quote's.
const productClassification = (row, record, relation) => {
  if (row[relation]?.name) return row[relation].name;
  const id = row[relation + '_id'];
  const quote = record.quote || {};
  const quoteId = quote[relation + '_id'] ?? quote[relation]?.id;
  return (id == null || String(id) === String(quoteId)) ? quote[relation]?.name || '' : '';
};

const productsOf = record => Array.isArray(record.products)
  ? record.products
  : record.product_code ? buildQuoteProducts(record) : [];

export const PO_SALES_CSV_COLUMNS = [
  ['PO Number', r => r.po_number],
  ['Quote No.', r => r.offer_no || r.system_quote_no],
  ['System Quote No.', r => r.system_quote_no],
  ['Customer', r => r.quote?.customer?.name],
  ['Company', r => r.quote?.company?.name],
  ['Branch', r => r.quote?.branch?.name],
  ['Principal', r => r.quote?.principal?.name],
  ['Category', r => r.quote?.category?.name],
  ['Sales Executive', r => r.quote?.sales_executive?.name],
  // Product columns take the row's own product; the PO columns repeat on every row.
  ['Product Code', (r, { product }) => product?.product_code],
  ['Product Principal / Vendor', (r, { product }) => product && productClassification(product, r, 'principal')],
  ['Product Category', (r, { product }) => product && productClassification(product, r, 'category')],
  ['Product Quantity', (r, { product }) => product?.qty],
  ['Product Unit Price', (r, { product }) => product?.unit_price],
  ['Product Total', (r, { product }) => product?.total],
  ['Quantity', r => r.qty],
  ['Quoted Total', r => r.total],
  ['Products Grand Total', r => { const p = productsOf(r); return p.length ? quoteProductSummary(p).total : ''; }],
  ['PO Amount', r => r.po_amount],
  ['PO Release Date', r => dateOnly(r.po_release_date)],
  ['PO Received Date', r => dateOnly(r.po_received_date)],
  ['Delivery Due Date', r => dateOnly(r.delivery_due_date)],
  ['PO to Principal/Vendor', r => r.po_to_principal_vendor],
  ['Stock Reference Number', r => r.stock_reference_number],
  ['Stock Reference Date', r => dateOnly(r.stock_reference_date)],
  ['Principal/Vendor Reference Number', r => r.principal_reference_number],
  ['Principal/Vendor Reference Date', r => dateOnly(r.principal_reference_date)],
  ['Payment Term', r => r.payment_term],
  ['PI', r => r.pi || 'No'],
  ['PI Reference Number', r => r.pi_reference_number],
  ['PI Date', r => dateOnly(r.pi_reference_date)],
  ['Status', r => r.status],
  ['Invoice Number', (r, { invoice }) => invoice?.invoice_no],
  ['Invoice Date', (r, { invoice }) => dateOnly(invoice?.invoice_date)],
  ['Invoice Transporter', (r, { invoice }) => invoice?.transporter_name],
  ['Invoice Reference Number', (r, { invoice }) => invoice?.reference_number],
  ['Transporter Name', r => r.transporter_name],
  ['Transit Reference Number', r => r.transit_reference_number],
  ['Transit Date', r => dateOnly(r.transit_date)],
  ['Freight', r => r.transit_freight],
  ['Remarks', r => r.remarks],
];

const escapeCell = value => {
  let text = value === null || value === undefined ? '' : String(value);
  // Stop spreadsheet apps from running user-entered text as a formula.
  if (/^[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = "'" + text;
  return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
};

export const buildPoSalesCsv = records => {
  const lines = [PO_SALES_CSV_COLUMNS.map(([label]) => escapeCell(label)).join(',')];
  records.forEach(record => {
    // One CSV row per product, so every PO field repeats and the PO stays easy to filter or pivot.
    // Invoices are independent of products: invoice N is listed on row N, and extra
    // invoices continue on further rows with blank product columns.
    const products = productsOf(record);
    const invoices = record.invoices || [];
    const rowCount = Math.max(products.length, invoices.length, 1);
    for (let index = 0; index < rowCount; index += 1) {
      const context = { product: products[index], invoice: invoices[index] };
      lines.push(PO_SALES_CSV_COLUMNS.map(([, pick]) => escapeCell(pick(record, context))).join(','));
    }
  });
  // BOM so Excel reads UTF-8 (e.g. the rupee sign) correctly.
  return '﻿' + lines.join('\r\n');
};

export const downloadPoSalesCsv = records => {
  const blob = new Blob([buildPoSalesCsv(records)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'po-sales-' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
